import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Seo } from "@/components/Seo";
import { Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const MARKETS = ["crypto", "forex", "stocks", "indices", "commodities"];
const LEVELS = ["beginner", "intermediate", "advanced"];
const RISKS = ["conservative", "moderate", "aggressive"];
const WATCH = ["BTC/USD", "ETH/USD", "EUR/USD", "GBP/USD", "NVDA", "AAPL", "SPX500", "XAU/USD"];

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [markets, setMarkets] = useState([]);
  const [level, setLevel] = useState("intermediate");
  const [risk, setRisk] = useState("moderate");
  const [watch, setWatch] = useState([]);

  useEffect(() => { if (user && user.onboarded) nav("/app"); }, [user]);

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const finish = async () => {
    await api.post("/auth/onboard", { preferred_markets: markets, experience: level, risk_preference: risk, watchlist: watch });
    await refresh();
    toast.success("Welcome to KREONIS!");
    nav("/app");
  };

  const steps = [
    { title: "Which markets interest you?", sub: "Pick the markets you want to focus on.",
      content: (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {MARKETS.map((m) => (
            <Choice key={m} active={markets.includes(m)} onClick={() => toggle(markets, setMarkets, m)} label={m} testid={`onb-market-${m}`} />
          ))}
        </div>
      ), valid: markets.length > 0 },
    { title: "Your experience level", sub: "This helps us tailor explanations.",
      content: <div className="grid gap-3">{LEVELS.map((l) => <Choice key={l} active={level === l} onClick={() => setLevel(l)} label={l} testid={`onb-level-${l}`} />)}</div>, valid: true },
    { title: "Risk preference", sub: "How much risk are you comfortable with?",
      content: <div className="grid gap-3">{RISKS.map((r) => <Choice key={r} active={risk === r} onClick={() => setRisk(r)} label={r} testid={`onb-risk-${r}`} />)}</div>, valid: true },
    { title: "Create your watchlist", sub: "Add a few assets to track. You can change these later.",
      content: (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {WATCH.map((w) => <Choice key={w} active={watch.includes(w)} onClick={() => toggle(watch, setWatch, w)} label={w} testid={`onb-watch-${w.replace(/\W/g, "-").toLowerCase()}`} />)}
        </div>
      ), valid: true },
  ];

  const s = steps[step];

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
        <Seo title="Onboarding" />
        <Logo />
        <div className="mt-8 flex items-center gap-2">
          {steps.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-primary" : "bg-secondary"}`} />)}
        </div>
        <div className="mt-8 rounded-3xl border border-border bg-card p-6 sm:p-8 fade-up">
          <h1 className="font-display text-2xl font-bold tracking-tight">{s.title}</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">{s.sub}</p>
          <div className="mt-6">{s.content}</div>
          <div className="mt-8 flex items-center justify-between">
            <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="rounded-xl px-4 py-2 text-sm text-muted-foreground disabled:opacity-40">Back</button>
            {step < steps.length - 1 ? (
              <button data-testid="onb-next" disabled={!s.valid} onClick={() => setStep(step + 1)} className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-50">
                Continue <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button data-testid="onb-finish" onClick={finish} className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground">
                Enter dashboard <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Choice({ active, onClick, label, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`flex items-center justify-between rounded-xl border px-4 py-3 text-sm font-medium capitalize transition-colors ${active ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-secondary"}`}>
      {label} {active && <Check className="h-4 w-4" />}
    </button>
  );
}
